import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";

import { BettingState } from "@ppb/betslip-core";
import {
  getBetslipGroup,
  getSportsbookConfirmationCombinations,
  getSportsbookConfirmationLegs,
  getIsBetslipLoaded,
} from "../../../store/state/betslip-module/betslip-module-selectors";
import { createGetCombinationOpportunity } from "../../../store/state/opportunities/opportunities-selectors";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../store/state/sportsbook-betting/betting-resolver";
import {
  createGetFailureLegIdsByCombinationGroupIdSelector,
  getSportsbookBettingImplyRunnerFailures,
} from "../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BetslipApplicationState } from "../../../store/state/BetslipApplicationState";
import { AlertViewModel } from "@ppb/the-wall-common/types";

import { createIsConfirmStep } from "../../sportsbook-betslip-confirm-mapper";
import { i18n } from "../../../helpers/i18n";
import { combinationToBetBuilder } from "../../connected-sportsbook-betslip-mapper";
import { DispatchProps, StateProps } from "../props";

import { CombinationContainerProps } from "./props";
import { getBetBuilderEventUrn, getUnavailableBetBuilderLegIds } from "../../../helpers/combinations";
import { createNotificationsViewModelBuilder } from "../Failure/bet-builder-failure-mapper";

const EMPTY_NOTIFICATIONS: AlertViewModel[] = [];

export const makeMapStateToProps: MapStateToPropsFactory<
  StateProps,
  CombinationContainerProps,
  BetslipApplicationState
> = () => {
  const labels = {
    odds: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stake: i18n({ key: "I18N.BETSLIP.STAKE" }),
    popular: i18n({ key: "I18N.BETSLIP.POPULAR" }),
    createdBets: i18n({ key: "I18N.BETSLIP.RAB" }),
  };
  const getFailedLegIdsByCombinationGroup = createGetFailureLegIdsByCombinationGroupIdSelector();
  const getCombinationOpportunity = createGetCombinationOpportunity();
  const getIsConfirmStep = createIsConfirmStep();
  const buildNotifications = createNotificationsViewModelBuilder();

  return (appState: BetslipApplicationState, { id, localization }: CombinationContainerProps) => {
    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    try {
      const isConfirmStep = getIsConfirmStep(appState);
      let legsMap: BettingState.LegsMap;
      let combination: BettingState.Combination;
      let failures: string[];

      if (isConfirmStep) {
        legsMap = getSportsbookConfirmationLegs(appState);
        combination = getSportsbookConfirmationCombinations(appState)[id];
        failures = [];
      } else {
        legsMap = getSportsbookBettingLegs(appState);
        combination = getSportsbookBettingCombinations(appState)[id];
        const failedLegIds = getFailedLegIdsByCombinationGroup(appState, combination?.combinationGroup);
        const unavailableLegIds =
          combination?.combinationGroup == null
            ? []
            : getUnavailableBetBuilderLegIds(
                combination.combinationGroup,
                combination.legs,
                legsMap,
                appState.betting.sportsbookBetting.runners,
                getSportsbookBettingImplyRunnerFailures(appState),
              );
        failures = [...unavailableLegIds, ...failedLegIds.filter((legId) => !unavailableLegIds.includes(legId))];
      }

      if (!Object.keys(legsMap).length || !Object.keys(combination).length) {
        return {};
      }

      const group = getBetslipGroup(appState);
      const resolvers = getBettingResolvers(group);
      const runnersMetadata = resolvers.getMetadata(appState);
      const runnersMapBettingState = appState.betting.sportsbookBetting.runners;
      const eventUrn = getBetBuilderEventUrn(runnersMetadata[legsMap[combination.legs[0]]?.runners[0]]);
      const eventFailures = isConfirmStep
        ? []
        : failures.filter(
            (legId) =>
              !!eventUrn &&
              legsMap[legId]?.runners.some((runnerId) => getBetBuilderEventUrn(runnersMetadata[runnerId]) === eventUrn),
          );

      const { selections, ...betBuilder } = combinationToBetBuilder(
        legsMap,
        runnersMetadata,
        localization,
        runnersMapBettingState,
      )(combination);

      const opportunityType = getCombinationOpportunity(appState, selections)?.type;

      const isPopular = opportunityType === "POPULAR";
      const isPackagedCreatedBets = opportunityType === "CREATED_BETS";

      return {
        ...betBuilder,
        legIds: combination.legs,
        failedLegIds: eventFailures,
        i18n: labels,
        isPopular,
        isPackagedCreatedBets,
        notifications: isConfirmStep
          ? EMPTY_NOTIFICATIONS
          : buildNotifications(
              appState,
              eventFailures.flatMap((legId) => legsMap[legId]?.runners ?? []),
            ),
      };
    } catch (e) {
      console.error(e);

      return {};
    }
  };
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, CombinationContainerProps> = () => ({});
